import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';

/* Helpers */
import get from 'lodash.get';
import {
    NUMBER_OF_PRODUCT_IMPRESSIONS,
    trackEcommerceProductImpressions,
    trackEcommerceProductClick,
} from '../../utils/tracking/searchBrowse/ecommerceTracking';
import { UNRESOLVED_SEARCH_CORRECTION } from '../useSearchCorrections';

class SbSharedItemTrackingProviderComponent extends React.Component {
    constructor() {
        super();

        this.state = {
            shouldFireImpression: false,
        };

        this.fireItemImpressionTracking = this.fireItemImpressionTracking.bind(this);
        this.fireItemClickTracking = this.fireItemClickTracking.bind(this);
        this.trackedItemImpressionIndexes = [];
        this.attemptedItemImpressionIndexes = [];
    }

    componentDidMount() {
        if (this.props.searchCorrectionTerm !== UNRESOLVED_SEARCH_CORRECTION) {
            this.fireItemImpressionTracking(0);
        }
    }

    componentDidUpdate(prevProps) {
        const regionalInfo = get(this.props, 'viewer.regionalInfo');
        const prevRegionalInfo = get(prevProps, 'viewer.regionalInfo');
        if (
            prevProps.searchCorrectionTerm === UNRESOLVED_SEARCH_CORRECTION &&
            this.props.searchCorrectionTerm !== UNRESOLVED_SEARCH_CORRECTION
        ) {
            this.fireItemImpressionTracking(0);
        }

        if (this.props.itemSearch.displayUriRef !== prevProps.itemSearch.displayUriRef) {
            /**
             * Assume the page changed and reset tracking data so that we're able to fire tracking
             * for the new set of items.
             */
            this.trackedItemImpressionIndexes = [];
            this.fireItemImpressionTracking(0);
        }

        if (!prevRegionalInfo && regionalInfo) {
            this.attemptedItemImpressionIndexes.forEach(attemptedItemImpressionIndex => {
                this.fireItemImpressionTracking(attemptedItemImpressionIndex);
            });
            this.attemptedItemImpressionIndexes = [];
        }
    }

    trackProductImpression(startIndex) {
        const { searchCorrectionTerm } = this.props;
        const items = get(this.props, 'itemSearch.edges');
        const pageType = get(this.props, 'itemSearch.pageType');
        const searchTerm = get(this.props, 'itemSearch.searchTerm');
        const userRegions = get(this.props, 'viewer.regionalInfo[0].regionsByZipCode') || [];

        trackEcommerceProductImpressions({
            items,
            pageType,
            searchTerm,
            startIndex,
            searchCorrectionTerm,
            userRegions,
        });
    }

    fireItemImpressionTracking(index) {
        const hasRegionalInfo = !!get(this.props, 'viewer.regionalInfo');
        /**
         * Group items from index 0 to 19 to group with the number `0`, from 20 to 39 to group
         * 1 and so on.
         */
        const impressionGroup = Math.floor(index / NUMBER_OF_PRODUCT_IMPRESSIONS);
        if (!hasRegionalInfo) {
            this.attemptedItemImpressionIndexes.push(impressionGroup);
        } else if (!this.trackedItemImpressionIndexes.includes(impressionGroup)) {
            /**
             * Very important to track indexes that have already fired - `react-lazy-load` can fire
             * `contentVisible` handler multiple times for the same item.
             */
            this.trackProductImpression(impressionGroup * NUMBER_OF_PRODUCT_IMPRESSIONS);
            this.trackedItemImpressionIndexes.push(impressionGroup);
        }
    }

    fireItemClickTracking(index) {
        const searchTerm = get(this.props, 'itemSearch.searchTerm');
        const pageType = get(this.props, 'itemSearch.pageType');
        const items = get(this.props, 'itemSearch.edges');
        const item = get(items, `[${index}].node.item`) || get(items, `[${index}].node`);
        const { searchCorrectionTerm } = this.props;
        const userRegions = get(this.props, 'viewer.regionalInfo[0].regionsByZipCode') || [];

        trackEcommerceProductClick({
            item,
            index,
            pageType,
            searchTerm,
            searchCorrectionTerm,
            userRegions,
        });
    }

    render() {
        return this.props.children({
            fireItemImpressionTracking: this.fireItemImpressionTracking,
            fireItemClickTracking: this.fireItemClickTracking,
        });
    }
}

SbSharedItemTrackingProviderComponent.propTypes = {
    children: PropTypes.func,
    searchCorrectionTerm: PropTypes.string,
    itemSearch: PropTypes.object.isRequired,
    viewer: PropTypes.object,
};

/*
  Fields necessary for ecommerce tracking...
  serviceId
  title
  seller { serviceId }
  categoryPath
  contemporaryTrackingString
  displayInfo(buyerId: $userId) @include(if: $isClient) {
    pricing {
        showNet
        showSale
    }
  }
  pricing {
    amount {
        convertedAmountList {
            amount
            currency
        }
    }
    salePrice {
        convertedAmountList {
            amount
            currency
        }
    }
    netPrice {
        convertedAmountList {
            amount
            currency
        }
    }
  }
*/
export const SbSharedItemTrackingProvider = createFragmentContainer(
    SbSharedItemTrackingProviderComponent,
    {
        itemSearch: graphql`
            fragment SbSharedItemTrackingProvider_itemSearch on ItemSearchQueryConnection {
                pageType
                searchTerm
                displayUriRef
                edges {
                    node {
                        item {
                            serviceId
                            title
                            categoryPath
                            browseUrl
                            categoryCode
                            contemporaryTrackingString
                            vertical
                            seller {
                                serviceId
                            }
                            displayPriceTracking: displayPrice(
                                page: searchAndBrowse
                                isTrade: $isTrade
                            ) {
                                convertedAmountList {
                                    amount
                                    currency
                                }
                                type
                            }
                            shipmentQuotes {
                                totalAmount {
                                    amount
                                }
                                serviceMethod {
                                    region {
                                        regionName
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `,
        viewer: graphql`
            fragment SbSharedItemTrackingProvider_viewer on Viewer {
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    regionsByZipCode {
                        displayName
                    }
                }
            }
        `,
    }
);
